import apiClient from '@shared/config/axiosConfig';
import imageCacheService from '../services/imageCache';

/**
 * Verifica si una URL de S3 ha expirado haciendo una petición HEAD
 * @param {string} url - URL a verificar
 * @returns {Promise<boolean>} - true si la URL es válida, false si ha expirado
 */
const isUrlStillValid = async (url) => {
    if (!url) return false;
    
    try {
        // Hacer una petición HEAD para verificar sin descargar el archivo
        const response = await fetch(url, { 
            method: 'HEAD',
            timeout: 5000 // 5 segundos de timeout
        });
        
        // Si la respuesta es exitosa, la URL es válida
        return response.ok;
    } catch (error) {
        console.warn(`ðŸ” URL parece inválida o expirada: ${url.substring(0, 50)}...`);
        return false;
    }
};

/**
 * Obtiene una imagen con manejo inteligente de expiración de URLs
 * @param {string} imagePath - Ruta del archivo en formato "folder/filename"
 * @returns {Promise<string>} - URL válida del archivo
 */
const getImage = async (imagePath) => {
    if (!imagePath) {
        console.warn('getImage: imagePath no proporcionado');
        return null;
    }

    try {
        // Intentar obtener la URL del caché primero
        let cachedUrl = imageCacheService.getFromCache(imagePath);
        
        if (cachedUrl) {
            // Verificar si la URL en caché sigue siendo válida
            const isValid = await isUrlStillValid(cachedUrl);
            if (isValid) {
                // console.log(`âœ… URL del caché verificada y válida: ${imagePath}`);
                return cachedUrl;
            } else {
                console.warn(`âš ï¸ URL del caché ha expirado, eliminando y renovando: ${imagePath}`);
                imageCacheService.removeFromCache(imagePath);
            }
        }

        // Verificar si ya hay una petición en vuelo para esta imagen
        if (imageCacheService.isPendingRequest(imagePath)) {
            // console.log(`â³ Esperando petición en vuelo para: ${imagePath}`);
            return await imageCacheService.getPendingRequest(imagePath);
        }

        // Si no está en caché o ha expirado, hacer petición a la API
        // console.log(`ðŸŒ Obteniendo nueva URL desde API para: ${imagePath}`);
        const slashIdx = imagePath.indexOf("/");
        const folder = imagePath.slice(0, slashIdx);
        const file = imagePath.slice(slashIdx + 1);

        // Crear la promesa de la petición con timeout aumentado.
        // encodeURIComponent es imprescindible: nombres con '#', '?', '&' o espacios
        // rompen la URL sin codificar y el backend recibe una clave truncada.
        const requestPromise = apiClient.get(`/files/${encodeURIComponent(file)}?folder=${encodeURIComponent(folder)}`, {
            timeout: 30000 // 30 segundos de timeout
        }).then(response => {
            const newUrl = response.data.url;
            // console.log(`âœ… Nueva URL obtenida desde API: ${newUrl.substring(0, 100)}...`);
            // Almacenar la nueva URL en el caché
            imageCacheService.setInCache(imagePath, newUrl);
            return newUrl;
        }).catch(error => {
            console.error(`âŒ Error en petición API para ${imagePath}:`, error.message);
            throw error;
        });

        // Almacenar la promesa en vuelo
        imageCacheService.setPendingRequest(imagePath, requestPromise);

        return await requestPromise;

    } catch (error) {
        console.error(`âŒ Error obteniendo imagen ${imagePath}:`, error);
        
        // En caso de error de red, intentar devolver URL cacheada aunque esté expirada
        if (error.code === 'ECONNABORTED' || error.message.includes('timeout') || 
            error.code === 'NETWORK_ERROR' || !navigator.onLine) {
            
            const cacheKey = imageCacheService.generateCacheKey(imagePath);
            const expiredEntry = imageCacheService.cache[cacheKey];
            if (expiredEntry && expiredEntry.url) {
                console.warn(`âš ï¸ Error de red - devolviendo URL expirada del caché para: ${imagePath}`);
                return expiredEntry.url;
            }
        }
        
        // Para otros errores, no devolver URL expirada
        throw error;
    }
};

const uploadFile = async (file, folder, customFileName = null) => {
    try {
        // Validaciones previas
        if (!file) {
            throw new Error("Archivo requerido");
        }
        
        if (!folder || folder.trim() === '') {
            throw new Error("Carpeta requerida");
        }

        // Validar tamaño del archivo (máximo 100MB)
        const maxSize = 100 * 1024 * 1024; // 100MB
        if (file.size > maxSize) {
            throw new Error("El archivo excede el tamaño máximo permitido (100MB)");
        }

        // Validar tipos de archivo
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'application/pdf'];
        if (!allowedTypes.includes(file.type)) {
            throw new Error(`Tipo de archivo no permitido. Tipos permitidos: ${allowedTypes.join(', ')}`);
        }

        // console.log(`ðŸ“¤ Iniciando subida: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)}MB) a ${folder}`);

        const formData = new FormData();
        formData.append("file", file);
        formData.append("folder", folder);
        
        // Si se proporciona un nombre personalizado, añadirlo al FormData
        if (customFileName) {
            formData.append("customFileName", customFileName);
        }

        // Configuración optimizada para subida de archivos
        const config = {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
            timeout: 300000, // 5 minutos para archivos grandes
            // Mostrar progreso si está disponible
            onUploadProgress: (progressEvent) => {
                const percentCompleted = Math.round(
                    (progressEvent.loaded * 100) / progressEvent.total
                );
                // console.log(`ðŸ“Š Progreso de subida: ${percentCompleted}%`);
            }
        };

        const response = await apiClient.post("/files", formData, config);
        
        // console.log(`âœ… Archivo subido exitosamente: ${response.data.key}`);
        return response.data;
        
    } catch (error) {
        console.error("âŒ Error detallado al subir archivo:", error);
        
        // Manejar errores específicos
        if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
            throw new Error("La subida del archivo tardó demasiado tiempo. Verifica tu conexión e intenta de nuevo.");
        } else if (error.response?.status === 413) {
            throw new Error("El archivo es demasiado grande para el servidor.");
        } else if (error.response?.status === 400) {
            throw new Error(error.response.data?.error || "Error de validación del archivo.");
        } else if (error.response?.status >= 500) {
            throw new Error("Error del servidor. Por favor, intenta de nuevo más tarde.");
        } else if (error.message.includes('Archivo requerido') || 
                   error.message.includes('Carpeta requerida') ||
                   error.message.includes('excede el tamaño') ||
                   error.message.includes('no permitido')) {
            throw error; // Re-lanzar errores de validación tal como están
        } else {
            throw new Error(`Error al subir archivo: ${error.message}`);
        }
    }
};

/**
 * Obtiene múltiples imágenes de manera optimizada usando caché
 * @param {Array} imagePaths - Array de rutas de imágenes
 * @param {Object} options - Opciones de configuración
 * @returns {Array} Array de objetos con imagePath y url
 */
const getMultipleImages = async (imagePaths, options = {}) => {
    const { 
        batchSize = 5, // Procesar en lotes de 5
        retryFailures = true 
    } = options;

    if (!Array.isArray(imagePaths) || imagePaths.length === 0) {
        return [];
    }

    // Eliminar duplicados de las rutas de entrada
    const uniquePaths = [...new Set(imagePaths.filter(path => path))];
    
    if (uniquePaths.length !== imagePaths.length) {
        // console.log(`ðŸ”„ Eliminados ${imagePaths.length - uniquePaths.length} duplicados de entrada`);
    }

    // console.log(`ðŸ”„ Procesando ${uniquePaths.length} imágenes únicas en lotes de ${batchSize}`);

    const results = [];
    const failures = [];

    // Procesar en lotes para evitar sobrecarga
    for (let i = 0; i < uniquePaths.length; i += batchSize) {
        const batch = uniquePaths.slice(i, i + batchSize);
        // console.log(`ðŸ“¦ Procesando lote ${Math.floor(i/batchSize) + 1}/${Math.ceil(uniquePaths.length/batchSize)}`);

        const batchPromises = batch.map(async (imagePath) => {
            try {
                const url = await getImage(imagePath);
                return { imagePath, url, success: true };
            } catch (error) {
                console.error(`Error procesando imagen ${imagePath}:`, error);
                failures.push(imagePath);
                return { imagePath, url: null, success: false, error };
            }
        });

        const batchResults = await Promise.all(batchPromises);
        results.push(...batchResults);

        // Pequeña pausa entre lotes para no saturar
        if (i + batchSize < uniquePaths.length) {
            await new Promise(resolve => setTimeout(resolve, 100));
        }
    }

    // Reintentar fallidos si está habilitado
    if (retryFailures && failures.length > 0) {
        // console.log(`ðŸ”„ Reintentando ${failures.length} imágenes fallidas`);
        for (const failedPath of failures) {
            try {
                await new Promise(resolve => setTimeout(resolve, 500)); // Espera más larga
                const url = await getImage(failedPath);
                const failedIndex = results.findIndex(r => r.imagePath === failedPath && !r.success);
                if (failedIndex !== -1) {
                    results[failedIndex] = { imagePath: failedPath, url, success: true };
                }
            } catch (error) {
                console.error(`Reintento fallido para ${failedPath}:`, error);
            }
        }
    }

    const successCount = results.filter(r => r.success).length;
    // console.log(`âœ… ${successCount}/${uniquePaths.length} imágenes procesadas exitosamente`);

    return results;
};

/**
 * Precarga imágenes en el caché sin devolverlas inmediatamente
 * Íštil para precargar imágenes que se usarán pronto
 */
const preloadImages = async (imagePaths, options = {}) => {
    const { silent = true } = options;
    
    if (!silent) {
        // console.log(`ðŸš€ Precargando ${imagePaths.length} imágenes en segundo plano`);
    }

    // Ejecutar en segundo plano sin bloquear
    setTimeout(async () => {
        try {
            await getMultipleImages(imagePaths, { ...options, batchSize: 3 });
            if (!silent) {
                // console.log('âœ… Precarga de imágenes completada');
            }
        } catch (error) {
            console.error('Error en precarga de imágenes:', error);
        }
    }, 100);
};

/**
 * Limpia el caché de imágenes - función de utilidad
 */
const clearImageCache = () => {
    imageCacheService.clearAllCache();
    // console.log('ðŸ—‘ï¸ Caché de imágenes limpiado');
};

/**
 * Obtiene estadísticas del caché de imágenes
 */
const getCacheStats = () => {
    return imageCacheService.getCacheStats();
};

/**
 * Fuerza la renovación de una URL específica
 * Íštil cuando se detecta que una URL ha expirado durante su uso
 * @param {string} imagePath - Ruta del archivo
 * @returns {Promise<string>} - Nueva URL válida
 */
const refreshImageUrl = async (imagePath) => {
    if (!imagePath) {
        console.warn('refreshImageUrl: imagePath no proporcionado');
        return null;
    }
    
    // console.log(`ðŸ”„ Forzando refresh de URL para: ${imagePath}`);
    
    // Eliminar del caché
    imageCacheService.removeFromCache(imagePath);
    
    // Limpiar peticiones pendientes
    imageCacheService.clearPendingRequest(imagePath);
    
    // Obtener nueva URL
    return await getImage(imagePath);
};

/**
 * Verifica si una respuesta de error indica que la URL ha expirado
 * @param {string} errorText - Texto del error
 * @returns {boolean} - true si indica expiración de URL
 */
const isS3UrlExpiredError = (errorText) => {
    if (!errorText || typeof errorText !== 'string') return false;
    
    return errorText.includes('<Code>AccessDenied</Code>') && 
           errorText.includes('<Message>Request has expired</Message>');
};

const fileService = {
    getImage,
    refreshImageUrl,
    isS3UrlExpiredError,
    uploadFile,
    getMultipleImages,
    preloadImages,
    clearImageCache,
    getCacheStats,
};

export default fileService;